import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  GlobalSeverityType,
  GlobalEventType,
  HeaderInput,
  HeaderTemplate,
  SectionHeader,
  SingleSelectionRadio,
  TitleHeader,
  useSnackbarHandler,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { RequestTypeMode } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import RouteKey from '../../../routes/key';
import AdminUserGroupDataTable from './AdminUserGroupDataTable';

const { admin } = RouteKey.child;
const { userGroupAccessConfiguration, createUserGroup } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
}));

const UserGroupListing = () => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { state } = useLocation<{
    requestType: RequestTypeMode;
    status?: GlobalSeverityType;
    event?: GlobalEventType;
  }>();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedRequestTypeMode, setSelectedRequestTypeMode] = useState<RequestTypeMode>(
    state?.requestType || RequestTypeMode.AdminUserGroup
  );

  const { data = [] } = useGetUserAuthorities();
  const hasCreatePermission = useMemo(
    () => data.includes(AUTHORITY_NAMES.CP_ADMIN_ADMIN_USER_GROUP_CREATE),
    [data]
  );
  const hasEditPermission = useMemo(
    () => data.includes(AUTHORITY_NAMES.CP_ADMIN_ADMIN_USER_GROUP_EDIT),
    [data]
  );
  const hasDeletePermission = useMemo(
    () => data.includes(AUTHORITY_NAMES.CP_ADMIN_ADMIN_USER_GROUP_DELETE),
    [data]
  );

  const handleNewUserGroup = useCallback(
    () => history.push(`/${admin}/${userGroupAccessConfiguration.route}/${createUserGroup.route}`),
    [history]
  );

  useEffect(() => {
    if (
      (state?.event === 'EDIT' &&
        state?.status === 'success' &&
        selectedRequestTypeMode === RequestTypeMode.AdminAccessConfiguration) ||
      (state?.event === 'ADD' &&
        state?.status === 'success' &&
        selectedRequestTypeMode === RequestTypeMode.AdminUserGroup)
    ) {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserGroups.ChangeRequestSuccessfullySentForApproval'),
      });
      history.replace({ state: {} });
    }
  }, [history, selectedRequestTypeMode, snackbarHandler, state?.event, state?.status, t]);

  return (
    <>
      <TitleHeader title={t('admin->>UserGroups.UserGroupAndAccessConfiguration')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('admin->>UserGroups.RequestType')}
          input={
            <SingleSelectionRadio
              data={[
                {
                  label: t('admin->>UserGroups.AdminUserGroup'),
                  value: RequestTypeMode.AdminUserGroup,
                },
                {
                  label: t('admin->>UserGroups.AdminAccessConfiguration'),
                  value: RequestTypeMode.AdminAccessConfiguration,
                },
              ]}
              onChange={(event) => {
                setSelectedRequestTypeMode(event.target.value as RequestTypeMode);
              }}
              value={selectedRequestTypeMode}
            />
          }
          topBottom={false}
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <Box className={classes.headerContainer}>
          <SectionHeader title={t('admin->>UserGroups.UserGroups')} />
          {hasCreatePermission && selectedRequestTypeMode === RequestTypeMode.AdminUserGroup && (
            <Button
              // className={classes.button}
              fullWidth={isMobile}
              onClick={handleNewUserGroup}
              id="button-new-user-group"
            >
              {t('admin->>UserGroups.NewUserGroup')}
            </Button>
          )}
        </Box>
        <AdminUserGroupDataTable
          requestType={selectedRequestTypeMode}
          hasEditPermission={hasEditPermission}
          hasDeletePermission={hasDeletePermission}
        />
      </ContentTemplate>
    </>
  );
};

export default UserGroupListing;
